import { useEffect, useRef, useState } from "react";
import { Bell, X, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { enablePush, pushFlags, pushSupport } from "@/lib/webpush";

/**
 * Banner discreto y no invasivo que se muestra en la esquina inferior derecha
 * (en mobile ocupa el ancho completo) solicitando permiso para Push.
 *
 * - Sólo aparece si el usuario inicia sesión (rol persona/automotora/taller/admin),
 *   el navegador soporta Push, y el usuario aún no dio respuesta.
 * - El botón "Activar" llama a enablePush() — gesto explícito requerido por Safari/iOS.
 * - "Ahora no" guarda un cooldown de 3 días en localStorage.
 */
export const PushPermissionBanner = ({ enabled = true }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    if (!visible) { root.style.setProperty("--tf-push-h", "0px"); return; }
    const measure = () => {
      const h = cardRef.current?.offsetHeight || 0;
      root.style.setProperty("--tf-push-h", h ? `${h + 12}px` : "0px");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      root.style.setProperty("--tf-push-h", "0px");
    };
  }, [visible]);

  useEffect(() => {
    if (!enabled) { setVisible(false); return; }
    // Pequeño delay para no competir con el primer render
    const t = setTimeout(() => setVisible(pushFlags.shouldShowBanner()), 1800);
    return () => clearTimeout(t);
  }, [enabled]);

  if (!visible) return null;

  const isIOSStandalone = typeof window !== "undefined" && window.navigator && window.navigator.standalone === true;
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const onEnable = async () => {
    setLoading(true);
    try {
      await enablePush();
      toast.success("Listo — las notificaciones están activas en este dispositivo.");
      setVisible(false);
    } catch (err) {
      const msg = err?.message || "No pudimos activar las notificaciones ahora.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onDismiss = () => {
    pushFlags.dismiss();
    setVisible(false);
  };

  // Caso iOS sin instalación PWA: no podemos suscribir, mostramos pista.
  const iosNotInstalled = isIOS && !isIOSStandalone;

  return (
    <div
      className="fixed tf-push-banner inset-x-3 sm:inset-x-auto sm:right-5 z-[55] tf-fade-up"
      data-testid="push-permission-banner"
    >
      <div ref={cardRef} className="relative max-w-sm rounded-2xl bg-[#13315C] text-white shadow-2xl border border-[#F5B301]/30 p-4 sm:p-5">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 active:scale-95 transition"
          aria-label="Cerrar"
          data-testid="push-banner-close"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3 mb-2">
          <span className="h-10 w-10 rounded-xl bg-[#F5B301] text-[#13315C] grid place-items-center shrink-0">
            <Bell size={18} />
          </span>
          <div>
            <p className="font-display font-bold text-base leading-tight">Recibe alertas al instante</p>
            <p className="text-xs text-white/70 mt-1">
              Te avisaremos cuando la notaría firme, el taller cotice o llegue tu pago — incluso si cierras la pestaña.
            </p>
          </div>
        </div>

        {iosNotInstalled ? (
          <div className="rounded-md bg-white/5 border border-white/10 p-3 mt-2 text-xs leading-relaxed">
            <p className="font-semibold mb-1 inline-flex items-center gap-1"><Sparkles size={12} className="text-[#F5B301]" /> En iPhone</p>
            <p className="text-white/70">
              Toca <span className="font-mono-tf">Compartir → Añadir a pantalla de inicio</span> para instalar Transfiérelo y luego activa las notificaciones desde la app.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
              className="mt-3 w-full bg-transparent border-white/30 text-white hover:bg-white/10 active:scale-[0.97] transition-transform"
              data-testid="push-banner-ios-ok"
            >
              Entendido
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
              className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 active:scale-[0.97] transition-transform"
              data-testid="push-banner-dismiss"
            >
              Ahora no
            </Button>
            <Button
              size="sm"
              onClick={onEnable}
              disabled={loading || !pushSupport.supported}
              className="flex-1 bg-[#F5B301] text-[#13315C] hover:bg-[#FFDC73] active:scale-[0.97] transition-transform font-bold"
              data-testid="push-banner-enable"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Activar"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushPermissionBanner;
